import React, { useState } from "react";
import { Container } from "reactstrap";
import SiteSettingsSection from "../components/SiteSettingsSection";
import SimpleFormModal from "../components/SimpleFormModal";
import NotificationToast from "../components/NotificationToast";
import InformationalModal from "../components/InformationalModal";
import { useAppData } from "../context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateSteeringPreference } from "../api/usersApi";


const Settings = () => {
  const data = useAppData();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState(null);
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });
  const timeout = parseInt(window.appConfig?.universalTimeout);

  function handleEdit(optiPref, initialPrefs) {
    setCurrentEdit({ optiPref, initialPrefs });
    setModalOpen(true);
  };

  async function handleSave(updatedData) {
    mutation.mutate({
      queryKey: [null, currentEdit.optiPref.id, updatedData, timeout],
    });
  };

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" })
  }

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateSteeringPreference,
    onSuccess: () => {
      queryClient.invalidateQueries(["customer_blob"]);
      setNotification({ visible: true, title: "Steering Preferences Edited", message: "Steering preferences for OptiSchedule have been edited" });
      setTimeout(() => {
        setNotification({ visible: false, title: "", message: "" });
      }, 2000);
      setCurrentEdit(null);
      setModalOpen(false);
    },
    onError: (err) => {
      setErrorMessage({ open: true, title: "Error Updating Steering Preferences", message: err.message });
      setCurrentEdit(null);
      setModalOpen(false);
    },
  });

  const fields = [
    { name: "cost_up", label: "Cost Up [€/Kwh]", type: "number" },
    { name: "cost_down", label: "Cost Down [€/Kwh]", type: "number" },
    { name: "min_energy", label: "Min Energy [Kwh]", type: "number" },
    { name: "max_energy", label: "Max Energy [Kwh]", type: "number" },
    { name: "initial_soc", label: "Initial SoC [0-100%]", type: "number" },
    { name: "terminal_soc", label: "Terminal SoC [0-100%]", type: "number" },
    { name: "objective", label: "Objective", type: "text" },
  ];

  return (
    <Container className="mb-5">
      <h4 className="mb-2  text-muted"><strong>{data.customer.display_name}</strong></h4>

      {data.sites.slice().sort((a, b) => a.id.localeCompare(b.id)).map((site) => {
        const siteId = site.id;

        const optiPref = data.steering_preferences.find(
          (p) => p.site_id === siteId && p.preference_type.toLowerCase() === "optischedule"
        );

        const defaultPrefs = optiPref?.preferences || null;

        let steeringPrefs = null;

        if (defaultPrefs !== null) {
          steeringPrefs = fields.map((f) => ({
            label: f.label,
            value: defaultPrefs[f.name] ?? "N/A",
          }));
        }

        const market = data.market_contexts.find((m) => m.site_id === siteId);
        const marketContext = market
          ? [
            { label: "System Operator", value: market.system_operator },
            { label: "EAN Code for Injection", value: market.ap_injection_ean },
            { label: "Energy Tariff Type", value: market.energy_tariff_type },
            { label: "Energy Price Param1", value: market.energy_price_param1 },
            { label: "Capacity Tariff Type", value: market.capacity_tariff_type },
          ]
          : [];

        const elec = data.electrical_systems.find((e) => e.site_id === siteId);
        const electricalSystem = elec
          ? [
            { label: "Has Grid Measurement", value: elec.has_grid_measurement ? "Yes" : "No" },
            { label: "PV AC Main Nominal Power (Wp)", value: elec.pv_ac_main_nominal_power },
            { label: "BESS Max Charge Power (W)", value: elec.bess_max_charge_power },
          ]
          : [];
        const devices = data.devices.filter((d) => d.site_id === siteId);

        return (
          <SiteSettingsSection
            key={site.id}
            site={site}
            steeringPrefs={steeringPrefs}
            marketContext={marketContext}
            electricalSystem={electricalSystem}
            devices={devices}
            onEdit={() => handleEdit(optiPref, defaultPrefs)}
          />
        );
      })}

      <InformationalModal
        isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message}
        onCancel={dismissInformationalModal}
      />

      <SimpleFormModal
        isOpen={modalOpen}
        title="OptiSchedule Preferences"
        fields={fields}
        initialData={currentEdit?.initialPrefs || {}}
        onSave={handleSave}
        onCancel={() => setModalOpen(false)}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}

    </Container>
  );
};

export default Settings;
